import React, {
    useContext,
    useEffect, 
    useMemo
} from 'react';
import makeStyles from '@mui/styles/makeStyles';
import CardContainer from '../../components/cardContainer';
import {
    Box, Typography, 
    useTheme
} from '@mui/material';
import Card from '../../components/card';
import {
    FormContext 
} from '../../../../contexts/form';
import {
    TypographyVariants 
} from '../../../../figmaTheme';

export const ChallengeSectionIntro = (): JSX.Element => {
    const formContext = useContext(FormContext);
    const section = useMemo(() => {
        return formContext?.sections.find(s => s.key === 'challenge');
    }, [formContext?.sections]);
    const useStyles = makeStyles(() => ({
        scrollableContainer: {
            overflow: 'scroll',
            scrollbarWidth: 'none',
            minHeight: '100%',
            width: '100vw',
            maxWidth: '692px',
            transform: 'translate(-24px, 0)',
            padding: '0 24px'
        },
        content: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
        },
        sections: {
            height: '80px',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            columnGap: '10px',
            marginTop: '50px',
            marginBottom: '32px'
        },
        section: {
            height: '80px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            rowGap: '8px'
        },
        background: {
            margin: 'auto 0',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '50%',
            width: '80px',
            height: '80px',
            alignItems: 'center',
            justifyContent: 'center',
            animation: '$explode 0.5s forwards linear 0.25s'
        },
        icon: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '80px',
            height: '80px',
            animation: '$fadeOut 0.1s forwards linear 0.25s'
        },
        challengeTitle: {
            zIndex: 2,
            animation: '$fadeOut 0.35s forwards 0s'
        },
        subtitle: {
            textAlign: 'center',
            marginTop: '32px',
            letterSpacing: 1,
            animation: '$fadeOut 0.35s forwards 0s'
        },
        cardTakeOver: {
            backgroundColor: 'white',
            height: '100%',
            width: '100%',
            maxWidth: '692px',
            position: 'absolute',
            top: 0,
            borderRadius: '24px 24px 0 0',
            transform: 'translate(0, 100vh)',
            animation: '$cardSlideUp 0.25s forwards 1s'
        },
        '@keyframes fadeIn': {
            '0%': {
                opacity: '0' 
            },
            '100%': {
                opacity: '1' 
            },
        },
        '@keyframes fadeOut': {
            '0%': {
                opacity: '1' 
            },
            '100%': {
                opacity: '0' 
            },
        },
        '@keyframes explode': {
            '0%': {
                backgroundColor: section?.colors.main,
                transform: 'scale(1.5)' 
            },
            '100%': {
                backgroundColor: section?.colors.background,
                transform: 'scale(40)' 
            },
        },
        '@keyframes backgroundSlideUp': {
            '0%': {
                transform: 'translate(0, calc(100vh + 24px))' 
            },
            '100%': {
                transform: 'translate(0, -24px)' 
            },
        },
        '@keyframes cardSlideUp': {
            '0%': {
                transform: 'translate(0, calc(100vh - 64px))' 
            },
            '100%': {
                transform: 'translate(0)' 
            },
        },
    }));
    const classes = useStyles();
    const theme = useTheme();

    useEffect(() => {
        setTimeout(() => {
            formContext?.goNext();
        }, 1250);
    }, []);

    return (
        <CardContainer
            sectionKey={'challenge'}
            backgroundColor={section?.showHeader ? theme.palette.success.main : theme.palette.white}
            headerTextColor={section?.showHeader ? theme.palette.white : null}
            hideHeaderText={!section?.showHeader}
        >
            <Card overflowVisible>
                <Box className={classes.content}>
                    {(section?.hasTrophy || section?.challengeGraphic) ? (
                        <Box className={classes.sections}>
                            <Box className={classes.section}>
                                <Box
                                    className={classes.background}
                                    style={{
                                        backgroundColor: section?.colors.main,
                                        fill: section?.colors.trophy
                                    }}
                                >
                                    <Box className={classes.icon}>
                                        {section?.hasTrophy && (
                                            <svg
                                                style={{
                                                    width: '50%',
                                                    height: '50%' 
                                                }}
                                                width="40"
                                                height="41"
                                                viewBox="0 0 40 41"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    d="M35 5.33337H33.2232C32.6022 3.59112 30.9522 2.33337 29 2.33337H11C9.047 2.33337 7.39775 3.59112 6.77675 5.33337H5C2.51825 5.33337 0.5 7.35162 0.5 9.83337V12.8334C0.5 16.4784 3.11675 19.5174 6.569 20.1886C7.20125 26.5044 12.2052 31.5451 18.5 32.2449V35.3334H14C13.172 35.3334 12.5 36.0046 12.5 36.8334C12.5 37.6621 13.172 38.3334 14 38.3334H26C26.828 38.3334 27.5 37.6621 27.5 36.8334C27.5 36.0046 26.828 35.3334 26 35.3334H21.5V32.2449C27.7948 31.5451 32.7987 26.5044 33.431 20.1886C36.8832 19.5174 39.5 16.4784 39.5 12.8334V9.83337C39.5 7.35162 37.4818 5.33337 35 5.33337ZM3.5 12.8334V9.83337C3.5 9.00537 4.17275 8.33337 5 8.33337H6.5V17.0566C4.75775 16.4356 3.5 14.7864 3.5 12.8334ZM36.5 12.8334C36.5 14.7864 35.2422 16.4356 33.5 17.0566V8.33337H35C35.8273 8.33337 36.5 9.00537 36.5 9.83337V12.8334Z"
                                                    fill="white"
                                                />
                                            </svg>
                                        )}
                                        {section?.challengeGraphic && (
                                            <>
                                                {section.challengeGraphic}
                                            </>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    ) : (
                        <Box
                            className={classes.background}
                            style={{
                                backgroundColor: theme.palette.white,
                                fill: theme.palette.white
                            }}
                        />
                    )}
                    <Box className={classes.challengeTitle}>
                        {section?.icon}
                    </Box>
                    <Typography
                        variant={TypographyVariants.H5}
                        className={classes.subtitle}
                        dangerouslySetInnerHTML={{
                            __html: section?.challengeDescription 
                        }}
                    />
                    <Box className={classes.cardTakeOver} />
                </Box>
            </Card>
        </CardContainer>
    );
};

export default ChallengeSectionIntro;
